import {useCallback, useEffect, useMemo} from 'react';

import {RolesAccount} from '@/app/auth/constants';
import {
  setProfile,
  setProfileMetadata,
  useAppDispatch,
  useAppSelector,
} from '@/app/store';
import {selectTokenData} from '@/app/store/tokenSlice';
import logError from '@/utils/errors/logError';

import {
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useLazyVerifySignUpCodeQuery,
  useUpdateProfileMetadataMutation,
  useUpdateSourceOfFundsMutation,
} from './api';
import {IUpdateProfileMetadataBody, SourceOfFundsType} from './entities';
import {mapVerificationUserToProfile} from './mapper';

export const useUsersRepository = () => {
  const tokenData = useAppSelector(selectTokenData);

  const {data, refetch: refetchProfile} = useGetProfileQuery(undefined, {
    skip: typeof tokenData?.accessToken === 'undefined',
  });

  const [triggerFetchProfileQuery] = useLazyGetProfileQuery();

  const [triggerUpdateProfileMetadataMutation] =
    useUpdateProfileMetadataMutation();

  const [triggerUpdateSourceOfFundsMutation] = useUpdateSourceOfFundsMutation();
  const dispatch = useAppDispatch();

  const profile = useMemo(() => {
    if (!data) return undefined;

    return mapVerificationUserToProfile(data);
  }, [data]);

  const isParent = useMemo(
    () => profile?.role === RolesAccount.PARENT,
    [profile?.role],
  );

  useEffect(() => {
    dispatch(setProfile(profile));
    dispatch(setProfileMetadata(profile?.metadata));
  }, [dispatch, profile]);

  const updateMetadata = useCallback(
    async (metadataUpdated: Record<string, any>): Promise<void> => {
      try {
        const body: IUpdateProfileMetadataBody = {
          metadata: {
            ...metadataUpdated,
          },
        };

        dispatch(
          setProfileMetadata({
            ...profile?.metadata,
            ...metadataUpdated,
          }),
        );

        await triggerUpdateProfileMetadataMutation(body).unwrap();
      } catch (error: any) {
        logError(error, {
          customTitle: 'Error updating profile metadata repository',
          fileName: 'users - useRepository.ts',
          functionName: 'updateMetadata',
          data: metadataUpdated,
        });
      }
    },
    [dispatch, profile?.metadata, triggerUpdateProfileMetadataMutation],
  );

  const updateSourceOfFunds = useCallback(
    async (sourceOfFunds: SourceOfFundsType): Promise<void> => {
      try {
        await triggerUpdateSourceOfFundsMutation({
          source: sourceOfFunds,
        }).unwrap();
      } catch (error: any) {
        logError(error, {
          customTitle: 'Error updating source of funds repository',
          fileName: 'users - useRepository.ts',
          functionName: 'updateSourceOfFunds',
          data: {sourceOfFunds},
        });
        throw error;
      }
    },
    [triggerUpdateSourceOfFundsMutation],
  );

  const [triggerVerifySignUpCode, {isFetching: isFetchingSignUpCode}] =
    useLazyVerifySignUpCodeQuery();

  return {
    profile,
    isParent,
    isFetchingSignUpCode,
    refetchProfile,
    updateMetadata,
    updateSourceOfFunds,
    triggerFetchProfileQuery,
    triggerVerifySignUpCode,
  };
};
